import {useEffect, useMemo, useState} from 'react';
import {getRewindStats} from './listeningHistory';
import './Rewind.css';

const formatNumber = value => value.toLocaleString();

function RewindStory({stats}) {
    const [index, setIndex] = useState(0);
    const slides = [
        {
            theme: 'lime', label: 'THE MAIN CHARACTER HAS ARRIVED',
            title: 'Your ears. Your era.',
            value: formatNumber(stats.events), unit: 'song moments',
            copy: 'An entire soundtrack to your life. Including the songs you politely escorted out.',
            detail: 'Your rewind starts here.',
        },
        {
            theme: 'purple', label: 'MUSIC WAS NOT A SIDE QUEST',
            title: 'You had a lot to listen to.',
            value: formatNumber(stats.minutes), unit: 'minutes of music',
            copy: `${stats.days} days of listening. Your headphones deserve paid time off.`,
            detail: 'Actual time played, not the full length of each song.',
        },
        {
            theme: 'pink', label: 'THE REPEAT BUTTON CALLED',
            title: stats.topTrack.name,
            value: formatNumber(stats.topTrack.plays), unit: 'times in your history',
            copy: 'A favorite? A coping strategy? We are not qualified to diagnose this.',
            detail: `${stats.topTrack.artist || 'Artist not provided'} / Your most-played song`,
        },
        {
            theme: 'orange', label: 'A VERY COMMITTED FAN CLUB',
            title: stats.topArtist?.name || 'A little mystery.',
            value: stats.topArtist ? formatNumber(stats.topArtist.plays) : '?',
            unit: stats.topArtist ? 'song moments with your top artist' : 'artist names in your export',
            copy: stats.topArtist ? 'At this point, you should probably be on the payroll.' : 'Your export kept the artist names secret. We respect the intrigue.',
            detail: 'Ranked by appearances in your history, including short plays.',
        },
        {
            theme: 'dark', label: 'THANK YOU, NEXT TRACK',
            title: stats.skips ? 'Your skip finger stayed busy.' : 'You gave every song a chance.',
            value: `${stats.skipRate}%`, unit: 'of song moments under 30 seconds',
            copy: stats.skips ? `${formatNumber(stats.skips)} quick exits. You know what you want. And, apparently, what you do not.` : 'No quick exits. A patient listener in an impatient world.',
            detail: 'Under 30 seconds is our quick-skip estimate, not proof you pressed skip.',
        },
        {
            theme: 'blue', label: 'YOUR TASTE CONTAINS MULTITUDES',
            title: 'One playlist could never.',
            value: formatNumber(stats.uniqueTracks), unit: 'different songs',
            copy: `${formatNumber(stats.uniqueArtists)} named artists. Your music taste refuses to fit in a single group chat.`,
            detail: 'Different songs are identified by their title and artist.',
        },
        {
            theme: 'lime', label: 'THAT IS A WRAP',
            title: 'Same ears. New lore.',
            value: formatNumber(stats.minutes), unit: 'minutes. Entirely your vibe.',
            copy: 'From the instant skips to the repeat-button romances, this soundtrack was unmistakably yours.',
            detail: 'Not an official Spotify Wrapped. Just you, decoded.',
        },
    ];
    const slide = slides[index];

    useEffect(() => {
        const handleKey = event => {
            const target = event.target;
            if (target instanceof HTMLElement && (target.matches('input, select, textarea, button, a') || target.isContentEditable)) return;
            if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                event.preventDefault();
                setIndex(current => Math.max(0, Math.min(slides.length - 1, current + (event.key === 'ArrowRight' ? 1 : -1))));
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [slides.length]);

    return (
        <div className="rewind-story">
            <div className="rewind-progress" aria-label="Slideshow progress">
                {slides.map((item, slideIndex) => (
                    <button key={item.label} className={slideIndex <= index ? 'is-complete' : ''}
                        aria-label={`Go to slide ${slideIndex + 1}: ${item.label}`}
                        aria-current={slideIndex === index ? 'step' : undefined}
                        onClick={() => setIndex(slideIndex)} />
                ))}
            </div>
            <section className={`rewind-card rewind-${slide.theme}`} aria-label={`Slide ${index + 1} of ${slides.length}`} aria-roledescription="slide">
                <div className="rewind-orbits" aria-hidden="true"><i /><i /><i /></div>
                <div className="rewind-card-top"><span>SKIP SIGNAL / REWIND</span><span>{String(index + 1).padStart(2, '0')} / 07</span></div>
                <div className="rewind-slide-content" key={index} aria-live="polite" aria-atomic="true">
                    <p className="rewind-label">{slide.label}</p>
                    <h2>{slide.title}</h2>
                    <p className="rewind-number">{slide.value}</p>
                    <p className="rewind-unit">{slide.unit}</p>
                    <p className="rewind-copy">{slide.copy}</p>
                    {index === slides.length - 1 && (
                        <div className="rewind-recap">
                            <span><b>{formatNumber(stats.uniqueArtists)}</b> artists</span>
                            <span><b>{stats.skipRate}%</b> quick skips</span>
                            <span><b>{formatNumber(stats.uniqueTracks)}</b> songs</span>
                        </div>
                    )}
                </div>
                <p className="rewind-detail">{slide.detail}</p>
            </section>
            <div className="rewind-controls">
                <button className="button" disabled={index === 0} onClick={() => setIndex(current => current - 1)}>Previous</button>
                <span>{index + 1} / {slides.length}<small>Use the arrow keys to explore</small></span>
                <button className="button button-primary" onClick={() => setIndex(current => current === slides.length - 1 ? 0 : current + 1)}>
                    {index === slides.length - 1 ? 'Replay' : 'Next'}
                </button>
            </div>
        </div>
    );
}

export default function Rewind({data, onUpload}) {
    const stats = useMemo(() => getRewindStats(data), [data]);
    return (
        <main className="rewind-main">
            <div className="rewind-heading">
                <div><p className="eyebrow">YOUR LISTENING, WITH A PLOT TWIST</p><h1>Your rewind.</h1></div>
                <a className="text-button" href="#overview">Back to dashboard</a>
            </div>
            <p className="rewind-intro">All your uploaded history. A few loving roasts. No Spotify connection needed.</p>
            {stats.events > 0 ? (
                <>
                    {stats.excluded > 0 && <p className="rewind-notice" role="status">{formatNumber(stats.excluded)} entries excluded: non-song entries or invalid song data.</p>}
                    <RewindStory stats={stats} />
                    <p className="rewind-footnote">Based on all uploaded files, not a single calendar year. Each song entry counts as a moment, even a short play.</p>
                </>
            ) : (
                <section className="rewind-empty">
                    <div className="rewind-empty-art" aria-hidden="true"><i /><i /><i /><i /><i /></div>
                    <p className="eyebrow">FIRST, GIVE US THE SOUNDTRACK</p>
                    <h2>{data ? 'No song moments found.' : 'Your story is waiting.'}</h2>
                    <p>{data ? 'This upload has no usable song entries. Choose Spotify streaming history with song titles and valid listening durations.' : 'Upload your Spotify streaming history JSON files to turn your listening into a very personal slideshow.'}</p>
                    <label className="button button-primary rewind-upload" htmlFor="rewind-upload">Choose history files
                        <input id="rewind-upload" type="file" accept=".json,application/json" multiple onChange={onUpload} />
                    </label>
                    <small>Your history stays in this browser session. Reloading clears it.</small>
                </section>
            )}
        </main>
    );
}
